import { useEffect, useState, type FormEvent } from 'react'
import { Modal } from './Modal'
import { supabase } from '@/lib/supabase'

/**
 * Modal de arqueo y cierre de caja.
 *
 * Flujo:
 *   1. Operador cuenta físicamente el efectivo en caja
 *   2. Ingresa el monto contado
 *   3. El RPC `cerrar_caja` calcula:
 *        teorico = apertura + ingresos efectivo - egresos efectivo (de la sesión)
 *        diferencial = real - teorico
 *   4. Si |diferencial| > Q 0.01 se exige motivo explicado
 *   5. Cierra la sesión, queda registrada con el arqueo completo
 */

type Props = {
  open: boolean
  onClose: () => void
  sesionId: string
  efectivoTeorico: number   // calculado en cliente para preview
  apertura: number
  onSuccess: () => void
}

export function CerrarCajaModal({
  open, onClose, sesionId, efectivoTeorico, apertura, onSuccess,
}: Props) {
  const [montoReal, setMontoReal] = useState<string>('')
  const [motivo, setMotivo] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const real = Number(montoReal) || 0
  const diferencial = real - efectivoTeorico
  const necesitaMotivo = Math.abs(diferencial) > 0.01

  useEffect(() => {
    if (!open) return
    setMontoReal(efectivoTeorico.toFixed(2))
    setMotivo('')
    setError(null)
  }, [open, efectivoTeorico])

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!montoReal || real < 0) {
      setError('Indicá el monto contado físicamente.')
      return
    }

    if (necesitaMotivo && motivo.trim().length < 4) {
      setError(`Diferencia de Q ${diferencial.toFixed(2)} requiere un motivo (mínimo 4 caracteres).`)
      return
    }

    setSubmitting(true)
    const { error: rpcErr } = await supabase.rpc('cerrar_caja', {
      p_sesion_id: sesionId,
      p_monto_real_contado: real,
      p_motivo_diferencial: necesitaMotivo ? motivo.trim() : null,
    })
    setSubmitting(false)

    if (rpcErr) {
      setError(`No se pudo cerrar la caja: ${rpcErr.message}`)
      return
    }

    onSuccess()
    onClose()
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Cerrar caja con arqueo"
      subtitle="Contá el efectivo físico antes de cerrar la sesión"
      size="md"
      blocking={submitting}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={submitting}>
            Cancelar
          </button>
          <button type="submit" form="cerrar-caja-form" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Cerrando…' : 'Cerrar caja'}
          </button>
        </>
      }
    >
      <form id="cerrar-caja-form" onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {error && <div className="error-banner" style={{ margin: 0 }}>{error}</div>}

        {/* Resumen del arqueo en formato kv-min */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          <div className="kv-arqueo">
            <span>Apertura</span>
            <span>Q {apertura.toFixed(2)}</span>
          </div>
          <div className="kv-arqueo">
            <span>Efectivo teórico</span>
            <span>Q {efectivoTeorico.toFixed(2)}</span>
          </div>
          <div className="kv-arqueo total">
            <span>Diferencia</span>
            <span style={{ color: necesitaMotivo ? (diferencial < 0 ? '#dc2626' : '#d97706') : 'var(--ok-ink)' }}>
              {diferencial >= 0 ? '+' : ''}Q {diferencial.toFixed(2)}
            </span>
          </div>
        </div>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted-foreground)', letterSpacing: 0.4, textTransform: 'uppercase' }}>
            Monto contado físicamente
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 16, color: 'var(--muted-foreground)' }}>Q</span>
            <input
              type="number"
              step="0.01"
              min="0"
              value={montoReal}
              onChange={(e) => setMontoReal(e.target.value)}
              required
              style={{ flex: 1, fontSize: 18, fontWeight: 600, fontFamily: 'var(--font-mono)' }}
              autoFocus
            />
          </div>
        </label>

        {necesitaMotivo && (
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: '#d97706', letterSpacing: 0.4, textTransform: 'uppercase' }}>
              Motivo del diferencial · obligatorio
            </span>
            <textarea
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder={diferencial < 0
                ? 'Ej. Faltante de billetes — necesita revisión'
                : 'Ej. Sobrante por moneda fraccionaria pendiente de regresar'
              }
              rows={2}
              required
              style={{ resize: 'vertical', minHeight: 60, fontSize: 13, fontFamily: 'inherit' }}
            />
          </label>
        )}

        <style>{`
          .kv-arqueo {
            display: flex; justify-content: space-between; align-items: center;
            padding: 10px 0;
            font-size: 13px;
            color: var(--ink-soft);
            border-bottom: 1px solid var(--hairline);
          }
          .kv-arqueo span:last-child {
            color: var(--ink); font-weight: 600;
            font-feature-settings: "tnum"; font-variant-numeric: tabular-nums;
          }
          .kv-arqueo.total {
            border-bottom: 0;
            padding-top: 12px;
            border-top: 1px solid var(--hairline);
            font-weight: 700;
            color: var(--ink);
          }
          .kv-arqueo.total span:last-child { font-weight: 800; font-size: 15px; }
        `}</style>
      </form>
    </Modal>
  )
}
